/**
 * Pure per-study status derivation for the Study Documents link-out cards
 * (Informed Consent Form, Visit & Form Matrix).
 *
 * These cards used to show a hardcoded "Built"/"Partial" badge that never
 * reflected the user's study. They now derive an honest badge from the real
 * underlying artifact, using the same vocabulary as the dynamic cards
 * ("Not started" vs "Ready"). Logic lives here so it is unit-testable without
 * React (web has no test runner; exercised via the functions package's vitest).
 */
export type StudyDocBadge = 'not_started' | 'in_progress' | 'ready';

export interface ConsentArtifactLike {
  status?: string;
  version?: number;
  /** Section-by-section progress written by the ArtifactBuilder. Absent on the
   *  auto-seed; present once the user has worked the consent in the builder. */
  builderState?: unknown;
}

/** The builder state's own "every section approved" flag, if it has one. */
function builderComplete(builderState: unknown): boolean | null {
  if (builderState == null || typeof builderState !== 'object') return null;
  const flag = (builderState as { isComplete?: unknown }).isComplete;
  return typeof flag === 'boolean' ? flag : null;
}

/**
 * generateAllFromProtocol seeds a consent artifact with status 'generated',
 * version 1, and no builderState. Treat that untouched seed — and a missing
 * consent — as "not started". Only "ready" once the consent has been worked on
 * or approved beyond the seed.
 *
 * "Worked on / approved beyond the seed" =
 *   - status advanced past the auto-seed ('in_review' / 'approved' / 'final'), OR
 *   - the section builder has been opened (builderState present), OR
 *   - the artifact has been re-saved past its initial version (version > 1).
 */
export function deriveConsentBadge(
  consent: ConsentArtifactLike | null | undefined,
): StudyDocBadge {
  if (!consent) return 'not_started';
  const status = consent.status;
  if (status === 'in_review' || status === 'approved' || status === 'final') {
    return 'ready';
  }
  // A form in the section builder is ready only when every section is
  // approved. One being built, or reopened because an approved section was
  // changed, is in progress.
  if (consent.builderState != null) return builderComplete(consent.builderState) === false ? 'in_progress' : 'ready';
  if (typeof consent.version === 'number' && consent.version > 1) return 'ready';
  return 'not_started';
}

export interface VisitMappingLike {
  visits?: Array<{ forms?: string[] }>;
}

/**
 * "Ready" only when a real visit/form matrix has been saved for the project —
 * the mapping doc exists AND at least one visit has at least one form assigned.
 * A missing doc, an empty doc, or a doc with no assignments is "not started".
 */
export function deriveVisitMatrixBadge(
  mapping: VisitMappingLike | null | undefined,
): StudyDocBadge {
  if (!mapping || !Array.isArray(mapping.visits)) return 'not_started';
  const hasAssignment = mapping.visits.some(
    (v) => Array.isArray(v.forms) && v.forms.length > 0,
  );
  return hasAssignment ? 'ready' : 'not_started';
}
